// client/src/App.jsx - полный код с вкладками
import React, { useState, useEffect, useMemo } from 'react';
import { Container, Spinner, Alert, Button, Tab, Tabs } from 'react-bootstrap';
import StatisticsCards from './components/StatisticsCards';
import SchoolsTable from './components/SchoolsTable';
import DistrictStats from './components/DistrictStats';
import Filters from './components/Filters';
import Visualization from './components/Visualization';
import ExportData from './components/ExportData';
import { getAllData, getSyncStatus, runSync } from './services/api';
import './App.css';

function App() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [allData, setAllData] = useState(null);
    const [syncStatus, setSyncStatus] = useState({});
    const [isSyncing, setIsSyncing] = useState(false);
    const [isFromCache, setIsFromCache] = useState(false);
    const [activeTab, setActiveTab] = useState('schools');
    const [filters, setFilters] = useState({ 
        district: 'all', 
        startDate: '', 
        endDate: '', 
        role: 'all' 
    });
    
    // Состояния для отслеживания прогресса загрузки
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [loadedBytes, setLoadedBytes] = useState(0);
    const [totalBytes, setTotalBytes] = useState(0);
    const [loadingStage, setLoadingStage] = useState('');

    useEffect(() => {
        loadAllData();
        loadSyncStatus();
        const interval = setInterval(loadSyncStatus, 60000);
        return () => clearInterval(interval);
    }, []);

    const loadAllData = async () => {
        setLoading(true);
        setError(null);
        setLoadingProgress(0);
        setLoadedBytes(0);
        setTotalBytes(0);
        setLoadingStage('Подготовка к загрузке...');
        
        try {
            const response = await fetch('https://apitestubcok.paragraf.spb.ru/api/all-data');
            
            const contentLength = response.headers.get('Content-Length');
            if (contentLength) {
                setTotalBytes(parseInt(contentLength));
            }
            
            const reader = response.body.getReader();
            const chunks = [];
            let receivedLength = 0;
            
            setLoadingStage('Загрузка данных...');
            
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                
                chunks.push(value);
                receivedLength += value.length;
                setLoadedBytes(receivedLength);
                
                if (contentLength) {
                    const progress = (receivedLength / parseInt(contentLength)) * 100;
                    setLoadingProgress(Math.min(Math.round(progress), 99));
                } else {
                    setLoadingProgress(Math.min(Math.round(receivedLength / 1000000 * 10), 95));
                }
            }
            
            setLoadingStage('Обработка данных...');
            setLoadingProgress(100);
            
            // Объединяем все чанки
            const chunksAll = new Uint8Array(receivedLength);
            let position = 0;
            for (const chunk of chunks) {
                chunksAll.set(chunk, position);
                position += chunk.length;
            }
            
            const result = JSON.parse(new TextDecoder('utf-8').decode(chunksAll));
            
            if (result.success) {
                setAllData(result);
                const currentYear = new Date().getFullYear();
                const startOfYear = `${currentYear}-01-01`;
                const today = new Date().toISOString().split('T')[0];
                
                setFilters(prev => ({
                    ...prev,
                    startDate: startOfYear,
                    endDate: today
                }));
                
                if (result.fromCache) {
                    setIsFromCache(true);
                    setTimeout(() => setIsFromCache(false), 3000);
                }
            } else {
                setError(result.error || 'Ошибка загрузки данных');
            }
        } catch (err) {
            console.error('❌ Ошибка:', err);
            setError(err.message || 'Ошибка подключения к серверу');
        } finally {
            setLoading(false);
        }
    };

    const loadSyncStatus = async () => {
        try {
            const status = await getSyncStatus();
            if (status.success) {
                setSyncStatus(status);
            }
        } catch (err) {
            console.error('Ошибка загрузки статуса:', err);
        }
    };

    const handleSync = async () => {
        setIsSyncing(true);
        try {
            await runSync();
            await fetch('/api/cache/refresh', { method: 'POST' });
            await loadAllData();
            await loadSyncStatus();
        } catch (err) {
            console.error('Ошибка синхронизации:', err);
            setError('Ошибка синхронизации данных');
        } finally {
            setIsSyncing(false);
        }
    };

    const handleApplyFilters = (newFilters) => {
        setFilters(newFilters);
    };

    const filteredData = useMemo(() => {
        if (!allData) return null;
        
        let filteredSchools = [...allData.schools];
        let filteredMonitoringData = [...allData.monitoringData];
        
        if (filters.district !== 'all') {
            filteredSchools = filteredSchools.filter(s => s.district === filters.district);
            filteredMonitoringData = filteredMonitoringData.filter(d => d.district === filters.district);
        }
        
        if (filters.startDate || filters.endDate) {
            filteredMonitoringData = filteredMonitoringData.filter(record => {
                if (!record.date) return false;
                let startOk = true;
                let endOk = true;
                if (filters.startDate) startOk = record.date >= filters.startDate;
                if (filters.endDate) endOk = record.date <= filters.endDate;
                return startOk && endOk;
            });
        }
        
        if (filters.role !== 'all') {
            filteredMonitoringData = filteredMonitoringData.filter(d => d.role === filters.role);
        }
        
        const schoolStatsMap = new Map();
        filteredMonitoringData.forEach(record => {
            if (!schoolStatsMap.has(record.school_id)) {
                schoolStatsMap.set(record.school_id, {
                    school_id: record.school_id,
                    total_records: 0,
                    total_views: 0,
                    unique_users: new Set(),
                    unique_students: new Set(),
                    unique_teachers: new Set(),
                    subjects: new Set(),
                    classes: new Set(),
                    providers: new Set(),
                    roles: new Set(),
                    dates: new Set()
                });
            }
            const stats = schoolStatsMap.get(record.school_id);
            stats.total_records++;
            stats.total_views += record.views_count || 0;
            stats.unique_users.add(record.user_alias);
            if (record.role === 'student') stats.unique_students.add(record.user_alias);
            if (record.role === 'teacher') stats.unique_teachers.add(record.user_alias);
            if (record.subject) stats.subjects.add(record.subject);
            if (record.class_level) stats.classes.add(record.class_level);
            if (record.provider) stats.providers.add(record.provider);
            if (record.role) stats.roles.add(record.role);
            if (record.date) stats.dates.add(record.date);
        });
        
        const schoolsWithStats = filteredSchools.map(school => {
            const stats = schoolStatsMap.get(school.id);
            return {
                id: school.id,
                school_name: school.school_name,
                district: school.district,
                total_records: stats?.total_records || 0,
                total_views: stats?.total_views || 0,
                unique_users: stats?.unique_users.size || 0,
                unique_students: stats?.unique_students.size || 0,
                unique_teachers: stats?.unique_teachers.size || 0,
                subjects_count: stats?.subjects.size || 0,
                classes_count: stats?.classes.size || 0,
                providers_count: stats?.providers.size || 0,
                active_days: stats?.dates.size || 0
            };
        }).sort((a, b) => b.total_views - a.total_views);
        
        const totalStats = {
            total_schools: filteredSchools.length,
            schools_with_data: schoolsWithStats.filter(s => s.total_views > 0).length,
            total_records: filteredMonitoringData.length,
            total_views: filteredMonitoringData.reduce((sum, d) => sum + (d.views_count || 0), 0),
            unique_users: new Set(filteredMonitoringData.map(d => d.user_alias)).size,
            unique_students: new Set(filteredMonitoringData.filter(d => d.role === 'student').map(d => d.user_alias)).size,
            unique_teachers: new Set(filteredMonitoringData.filter(d => d.role === 'teacher').map(d => d.user_alias)).size,
            total_providers: new Set(filteredMonitoringData.map(d => d.provider).filter(p => p)).size
        };
        
        return {
            schools: schoolsWithStats,
            totalStats,
            districts: allData.districts
        };
    }, [allData, filters]);

    const formatBytes = (bytes) => {
        if (bytes === 0) return '0 MB';
        const mb = bytes / (1024 * 1024);
        return mb.toFixed(2) + ' MB';
    };

    if (loading) {
        const radius = 80;
        const circumference = 2 * Math.PI * radius;
        const strokeDashoffset = circumference - (loadingProgress / 100) * circumference;
        
        return (
            <div className="loading-container">
                <div className="loading-card-circular">
                    <div className="circular-progress">
                        <svg className="circular-svg" viewBox="0 0 220 220">
                            <defs>
                                <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                    <stop offset="0%" stopColor="#0d6efd" />
                                    <stop offset="100%" stopColor="#0099ff" />
                                </linearGradient>
                            </defs>
                            <circle
                                className="progress-bg"
                                cx="110"
                                cy="110"
                                r={radius}
                                strokeWidth="12"
                                fill="none"
                            />
                            <circle
                                className="progress-fill"
                                cx="110"
                                cy="110"
                                r={radius}
                                strokeWidth="12"
                                fill="none"
                                strokeDasharray={circumference}
                                strokeDashoffset={strokeDashoffset}
                            />
                        </svg>
                        <div className="progress-percent-center">
                            <span className="percent-number">{loadingProgress}%</span>
                            <span className="percent-label">загружено</span>
                        </div>
                    </div>
                    <div className="loading-details">
                        <div className="loading-stage-circular">{loadingStage}</div>
                        <div className="loading-size-circular">
                            {totalBytes > 0 ? (
                                <>
                                    <i className="bi bi-hdd-stack me-1"></i>
                                    {formatBytes(loadedBytes)} / {formatBytes(totalBytes)}
                                </>
                            ) : (
                                <i className="bi bi-arrow-repeat me-1"></i>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <Container className="mt-5">
                <Alert variant="danger">
                    <Alert.Heading>Ошибка загрузки данных</Alert.Heading>
                    <p>{error}</p>
                    <Button onClick={loadAllData}>Повторить</Button>
                </Alert>
            </Container>
        );
    }

    if (!filteredData) return null;

    const formatDate = (dateString) => {
        if (!dateString) return 'нет данных';
        const date = new Date(dateString);
        return date.toLocaleDateString('ru-RU', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric'
        });
    };

    const lastSyncDate = syncStatus.max_date 
        ? formatDate(syncStatus.max_date)
        : 'нет данных';

    return (
        <div className="app">
            <header className="app-header">
                <Container>
                    <div className="d-flex justify-content-between align-items-start">
                        <div>
                            <h1>
                                <i className="bi bi-bar-chart"></i>
                                Мониторинг УБ ЦОК
                            </h1>
                            <p>Санкт-Петербург • Аналитическая панель</p>
                        </div>
                        <div className="text-end">
                            <div className="sync-info">
                                <span><i className="bi bi-database"></i> {(syncStatus.total_records || 0).toLocaleString()}</span>
                                <span><i className="bi bi-people"></i> {(syncStatus.unique_users || 0).toLocaleString()}</span>
                                <span><i className="bi bi-building"></i> {(syncStatus.schools_with_data || 0).toLocaleString()}</span>
                                <span><i className="bi bi-truck"></i> {(syncStatus.providers_count || 0).toLocaleString()} поставщиков</span>
                                <button onClick={handleSync} className="sync-btn" disabled={isSyncing}>
                                    {isSyncing ? (
                                        <><Spinner animation="border" size="sm" className="me-1" /> Синхронизация...</>
                                    ) : (
                                        <><i className="bi bi-arrow-repeat"></i></>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="sync-date-info mt-2">
                        <i className="bi bi-cloud-check me-1"></i>
                        Последняя синхронизация с региональным сервером: <strong>{lastSyncDate}</strong>
                        <span className="ms-2 text-muted small">
                            (актуальность данных)
                        </span>
                    </div>
                </Container>
            </header>

            <Container className="mt-4">
                <Filters 
                    districts={filteredData.districts || []} 
                    filters={filters}
                    onApplyFilters={handleApplyFilters}
                    dateRange={allData?.dateRange}
                />

                <StatisticsCards stats={filteredData.totalStats} />

                <Tabs
    activeKey={activeTab}
    onSelect={(k) => setActiveTab(k)}
    className="mb-4"
>
    <Tab eventKey="schools" title="Образовательные учреждения">
        <SchoolsTable schools={filteredData.schools || []} allData={allData} />
    </Tab>
    <Tab eventKey="districts" title="Статистика по районам">
        <DistrictStats 
            allData={allData} 
            filters={filters}
            onDistrictSelect={(district) => {
                setFilters(prev => ({ ...prev, district }));
                setActiveTab('schools');
            }}
        />
    </Tab>
    <Tab eventKey="visualization" title="Визуализация">
        <div className="visualization-container">
            <Visualization allData={allData} filters={filters} />
        </div>
    </Tab>
    <Tab eventKey="export" title="Экспорт данных">
        <ExportData allData={allData} filters={filters} />
    </Tab>
</Tabs>
            </Container>

            {isFromCache && (
                <div className="cache-indicator">
                    <i className="bi bi-database"></i> Данные из кеша
                </div>
            )}
        </div>
    );
}

export default App;